/**
 * DischargePage Component
 * Main page for the Discharge feature with Discharge / History tabs.
 * Follows PowerReportingPage patterns: OathPageWrapper, OathHeaderCard,
 * Headless UI TabGroup, withErrorBoundary HOC.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react';
import {
  DocumentCheckIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { useOathAuth } from '../../../services/oathAuthContext';
import {
  validatePowers,
  submitDischarge,
  getHistory,
} from '../../../services/oathDischargeService';
import { useDischargeQueue } from '../../../hooks/useDischargeQueue';
import { useDrafts } from '../../../hooks/useDrafts';
import {
  OathPageWrapper,
  OathHeaderCard,
  OathContentCard,
  OathAgencySelector,
  OathSpinner,
  OathAlert,
  OathModal,
} from '../../shared/OathUI';
import PowerNumberEntry from './components/PowerNumberEntry';
import DischargeResultsSummary from './components/DischargeResultsSummary';
import DischargeHistoryTable from './components/DischargeHistoryTable';
import DraftsList from './components/DraftsList';
import ConfirmDischargeModal from './modals/ConfirmDischargeModal';
import ProofUploadModal from './modals/ProofUploadModal';
import { withErrorBoundary } from './components/shared';
import { categorizeValidations, mapValidationToDisplay, validatePowerForDischarge } from './utils/dischargeValidation';
import { DISCHARGE_TABS, HISTORY_TAB_STATUSES } from './utils/constants';

const DischargePage = () => {
  const { user } = useOathAuth();

  // Agency selection
  const agencies = useMemo(() => user?.agencies || [], [user?.agencies]);
  const [selectedAgencyId, setSelectedAgencyId] = useState(null);
  const effectiveAgencyId = selectedAgencyId ?? agencies[0]?.agencyId ?? null;

  // Tab state (persisted in URL search params)
  const [activeTab, setActiveTabRaw] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = parseInt(params.get('tab'), 10);
    return [DISCHARGE_TABS.DISCHARGE, DISCHARGE_TABS.PENDING, DISCHARGE_TABS.COMPLETED].includes(tab)
      ? tab
      : DISCHARGE_TABS.DISCHARGE;
  });
  const setActiveTab = useCallback((tab) => {
    setActiveTabRaw(tab);
    const params = new URLSearchParams(window.location.search);
    if (tab === DISCHARGE_TABS.DISCHARGE) {
      params.delete('tab');
    } else {
      params.set('tab', tab);
    }
    const query = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  }, []);

  // Draft management
  const {
    drafts,
    isLoadingDrafts,
    activeReportId,
    activeDraftMeta,
    createDraft: handleCreateDraft,
    resumeDraft,
    abandonDraft,
    exitDraft,
    refreshDrafts,
  } = useDrafts({ agencyId: effectiveAgencyId });

  // Powers data — persisted to backend queue (scoped to draft when active)
  const {
    selectedPowerNos,
    setSelectedPowerNos,
    handleRemovePower: queueRemovePower,
    handleClearAll: queueClearAll,
    isLoadingQueue,
    powerDetailsMap,
    updatePowerDetails,
    reloadQueue,
    savingPowerNos,
    addUnverifiedPowers,
  } = useDischargeQueue({ agencyId: effectiveAgencyId, reportId: activeReportId });

  // Validation state
  const [validationResult, setValidationResult] = useState(null);
  const [validationData, setValidationData] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  // Reason & comments
  const [reason, setReason] = useState('');
  const [agentComments, setAgentComments] = useState('');

  // Proof state: Map<powerNo, { fileId: string, fileName: string }>
  const [proofsByPower, setProofsByPower] = useState(new Map());

  // Submit state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  // Modal state
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [proofModalOpen, setProofModalOpen] = useState(false);
  const [proofModalPower, setProofModalPower] = useState(null);

  // History tab counts (reported by DischargeHistoryTable via onItemsLoaded)
  const [historyStatusCounts, setHistoryStatusCounts] = useState({});
  const handleHistoryItemsLoaded = useCallback((items) => {
    const counts = {};
    items.forEach((item) => {
      counts[item.status] = (counts[item.status] || 0) + 1;
    });
    setHistoryStatusCounts(counts);
  }, []);
  const pendingCount = (historyStatusCounts['Pending Review'] || 0) + (historyStatusCounts['Failed'] || 0);

  // Pre-fetch history on mount to populate tab badge counts before the user visits those tabs
  useEffect(() => {
    let cancelled = false;
    getHistory(1, 500).then((response) => {
      if (cancelled || !response.success || !response.data) return;
      const items = response.data.items || [];
      const counts = {};
      items.forEach((item) => {
        counts[item.status] = (counts[item.status] || 0) + 1;
      });
      setHistoryStatusCounts(counts);
    });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ========================================================================
  // CLIENT-SIDE VALIDATION MAP (instant UX hints from local data)
  // ========================================================================

  const clientValidationMap = useMemo(() => {
    const map = new Map();
    // POST /validate response is the freshest server truth — check first
    const validateResponseMap = new Map((validationData || []).map((v) => [v.powerNo, v]));

    for (const powerNo of selectedPowerNos) {
      const serverV = validateResponseMap.get(powerNo);
      if (serverV) {
        if (!serverV.isValid) {
          const errors = Array.isArray(serverV.validationErrors) ? serverV.validationErrors : [];
          map.set(powerNo, {
            status: 'blocked',
            reason: errors.join(' ') || 'This power cannot be discharged.',
          });
          continue;
        }
        if (serverV.requiresProof) {
          map.set(powerNo, {
            status: 'needsProof',
            reason: serverV.proofReason || 'Proof of discharge required.',
          });
          continue;
        }
        map.set(powerNo, { status: 'ready' });
        continue;
      }

      const details = powerDetailsMap.get(powerNo);
      if (!details?.status) continue;

      // Prefer server validation from queue add response (authoritative)
      if (details.serverValidation) {
        map.set(powerNo, details.serverValidation);
      } else {
        // Fall back to client-side pre-validation (instant UX hint before server responds)
        const result = validatePowerForDischarge({
          powerStatus: details.status,
          state: details.state,
          liability: details.bondAmount,
          batchClosed: details.batchClosed,
          forfeitureStatus: details.isForfeiture ? 'F' : null,
        });
        map.set(powerNo, result);
      }
    }
    return map;
  }, [selectedPowerNos, powerDetailsMap, validationData]);

  // ========================================================================
  // SUBMIT DISCHARGE (validate → check → confirm modal → submit)
  // ========================================================================

  const handleSubmitDischarge = useCallback(async () => {
    if (selectedPowerNos.size === 0) {
      toast.warning('Enter at least one power number to discharge.');
      return;
    }

    // Check for blocked powers (client-side)
    const blockedPowers = [];
    for (const [powerNo, v] of clientValidationMap) {
      if (v.status === 'blocked') blockedPowers.push(powerNo);
    }
    if (blockedPowers.length > 0) {
      toast.error(`${blockedPowers.length} power(s) cannot be discharged. Remove blocked powers before submitting.`);
      return;
    }

    setIsValidating(true);

    try {
      const powerNumbers = Array.from(selectedPowerNos);
      const response = await validatePowers(powerNumbers, reason, agentComments, {
        agencyId: effectiveAgencyId,
        excludeCallersQueue: true,
      });

      if (response.success && response.data) {
        const validations = response.data.validations || [];
        setValidationData(validations);

        // Reconcile POST /validate with clientValidationMap (queue-add server validation).
        // If EITHER source says proof is required, categorize as needsProof.
        const serverMap = new Map(validations.map((v) => [v.powerNo, v]));
        const ready = [];
        const needsProof = [];
        const invalid = [];

        for (const powerNo of powerNumbers) {
          const serverV = serverMap.get(powerNo);
          const display = serverV ? mapValidationToDisplay(serverV) : { powerNo };

          if (serverV && !serverV.isValid) {
            invalid.push(display);
          } else if (serverV?.requiresProof || clientValidationMap.get(powerNo)?.status === 'needsProof') {
            needsProof.push(display);
          } else {
            ready.push(display);
          }
        }

        setValidationResult({ ready, needsProof, invalid });

        // Check for server-side invalid powers
        if (invalid.length > 0) {
          const firstError = invalid.find((p) => p.validationErrors?.length)?.validationErrors?.[0];
          const suffix = firstError
            ? ` ${invalid.length === 1 ? firstError : `First error: ${firstError}`}`
            : ' Review the highlighted rows and remove invalid powers.';
          toast.error(`${invalid.length} power(s) failed validation.${suffix}`);
          setIsValidating(false);
          return;
        }

        // All valid — open confirmation modal
        setConfirmModalOpen(true);
      } else {
        toast.error(response.error || 'Validation failed.');
      }
    } catch (err) {
      console.error('Validation error:', err);
      toast.error('An error occurred during validation.');
    } finally {
      setIsValidating(false);
    }
  }, [selectedPowerNos, reason, agentComments, effectiveAgencyId, clientValidationMap]);

  // ========================================================================
  // REMOVE POWER
  // ========================================================================

  const handleRemovePower = useCallback((powerNo) => {
    // Remove from queue (syncs to backend)
    queueRemovePower(powerNo);

    // Also remove from validation result
    setValidationResult((prev) => {
      if (!prev) return prev;
      return {
        ready: prev.ready.filter((p) => p.powerNo !== powerNo),
        needsProof: prev.needsProof.filter((p) => p.powerNo !== powerNo),
        invalid: prev.invalid.filter((p) => p.powerNo !== powerNo),
      };
    });

    // Clean up proof data
    setProofsByPower((prev) => {
      const next = new Map(prev);
      next.delete(powerNo);
      return next;
    });
  }, [queueRemovePower]);

  // ========================================================================
  // PROOF UPLOAD
  // ========================================================================

  const handleOpenProofModal = useCallback((powerNo) => {
    // Try server validation data first, then fall back to local data
    const serverData = validationData?.find((v) => v.powerNo === powerNo);
    const localData = powerDetailsMap.get(powerNo);
    setProofModalPower(serverData || {
      powerNo,
      bondAmount: localData?.bondAmount,
      defendantName: localData?.defendantName,
      proofReason: clientValidationMap.get(powerNo)?.reason,
    });
    setProofModalOpen(true);
  }, [validationData, powerDetailsMap, clientValidationMap]);

  // Single handler for proof add/delete — modal calls with 4th arg `isDeleted`
  const handleProofUpdated = useCallback((powerNo, fileId, fileName, isDeleted = false) => {
    setProofsByPower((prev) => {
      const next = new Map(prev);
      if (isDeleted) {
        next.delete(powerNo);
      } else {
        next.set(powerNo, { fileId, fileName });
      }
      return next;
    });
  }, []);

  // ========================================================================
  // CONFIRM & SUBMIT DISCHARGE
  // ========================================================================

  const handleConfirmSubmit = useCallback(async () => {
    setIsSubmitting(true);
    setConfirmModalOpen(false);

    try {
      // Collect all proof document IDs
      const allProofIds = [];
      proofsByPower.forEach((proof) => {
        if (proof.fileId) allProofIds.push(proof.fileId);
      });

      // Build power numbers from ready + needsProof
      const powerNumbers = [
        ...(validationResult?.ready || []).map((p) => p.powerNo),
        ...(validationResult?.needsProof || []).map((p) => p.powerNo),
      ];

      const response = await submitDischarge({
        powerNumbers,
        agencyId: effectiveAgencyId,
        reportId: activeReportId || undefined,
        reason: reason || undefined,
        agentComments: agentComments || undefined,
        proofDocumentIds: allProofIds.length > 0 ? allProofIds : undefined,
      });

      if (response.success && response.data) {
        // Reconcile submit response with validationResult so the success page
        // matches what the confirm modal showed. The backend may categorize
        // differently; override using the frontend's reconciled validation.
        const needsProofSet = new Set(
          (validationResult?.needsProof || []).map((p) => p.powerNo),
        );
        const serverDischarged = response.data.dischargedPowers || [];
        const serverPending = response.data.pendingPowers || [];
        const reconciledDischarged = serverDischarged.filter((no) => !needsProofSet.has(no));
        const reconciledPending = [
          ...new Set([
            ...serverPending,
            ...serverDischarged.filter((no) => needsProofSet.has(no)),
          ]),
        ];

        setSubmitResult({
          ...response.data,
          dischargedPowers: reconciledDischarged,
          pendingPowers: reconciledPending,
          processedCount: reconciledDischarged.length,
          pendingReviewCount: reconciledPending.length,
        });
        toast.success(response.data.message || 'Discharge submitted successfully.');
        // Clear queue — these powers are now discharged
        queueClearAll();
        // Exit draft mode and refresh drafts list
        exitDraft();
        refreshDrafts();
      } else {
        toast.error(response.error || 'Discharge submission failed.');
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('An error occurred during submission.');
    } finally {
      setIsSubmitting(false);
    }
  }, [proofsByPower, validationResult, reason, agentComments, queueClearAll, effectiveAgencyId, activeReportId, exitDraft, refreshDrafts]);

  // ========================================================================
  // POST-SUBMIT ACTIONS
  // ========================================================================

  const resetFormState = useCallback(() => {
    setValidationResult(null);
    setValidationData(null);
    setProofsByPower(new Map());
    setReason('');
    setAgentComments('');
    setSubmitResult(null);
  }, []);

  const handleDischargeMore = useCallback(() => {
    // Reset all state for a fresh discharge
    queueClearAll();
    exitDraft();
    resetFormState();
    setActiveTab(DISCHARGE_TABS.DISCHARGE);
  }, [queueClearAll, exitDraft, resetFormState]);

  const handleViewHistory = useCallback(() => {
    setSubmitResult(null);
    setActiveTab(DISCHARGE_TABS.COMPLETED);
  }, []);

  // ========================================================================
  // DRAFT ACTIONS
  // ========================================================================

  const handleResumeDraft = useCallback(async (reportId) => {
    const draft = await resumeDraft(reportId);
    if (draft) {
      // Queue will auto-reload via useEffect (reportId dependency changes)
      resetFormState();
      setActiveTab(DISCHARGE_TABS.DISCHARGE);
    }
  }, [resumeDraft, resetFormState]);

  const handleStartFresh = useCallback(() => {
    exitDraft();
    resetFormState();
    setActiveTab(DISCHARGE_TABS.DISCHARGE);
  }, [exitDraft, resetFormState]);

  const handleAbandonActiveDraft = useCallback(async () => {
    if (!activeReportId) return;
    const success = await abandonDraft(activeReportId);
    if (success) {
      resetFormState();
      setActiveTab(DISCHARGE_TABS.DISCHARGE);
    }
  }, [activeReportId, abandonDraft, resetFormState]);

  // ========================================================================
  // COMPUTED
  // ========================================================================

  const totalLiability = useMemo(() => {
    if (!validationResult) return 0;
    const sum = (arr) => arr.reduce((acc, p) => acc + (p?.liability || 0), 0);
    return sum(validationResult.ready) + sum(validationResult.needsProof);
  }, [validationResult]);

  // ========================================================================
  // RENDER
  // ========================================================================

  return (
    <OathPageWrapper>
      {/* Header */}
      <OathHeaderCard
        title="Power Discharges"
        icon={DocumentCheckIcon}
        compact
      >
        {agencies.length > 1 && (
          <OathAgencySelector
            agencies={agencies}
            selectedAgencyId={effectiveAgencyId}
            onSelect={(id) => {
              setSelectedAgencyId(id);
              // Reset selection and draft state when agency changes
              queueClearAll();
              exitDraft();
              resetFormState();
            }}
          />
        )}
      </OathHeaderCard>

      {/* Post-submit results modal */}
      <OathModal
        isOpen={!!submitResult}
        onClose={handleDischargeMore}
        title="Discharge Results"
        size="xl"
      >
        {submitResult && (
          <DischargeResultsSummary
            result={submitResult}
            validationData={validationData}
            onDischargeMore={handleDischargeMore}
            onViewHistory={handleViewHistory}
          />
        )}
      </OathModal>

      {/* Main tabbed content */}
      <OathContentCard noPadding className="!overflow-visible">
          <TabGroup selectedIndex={activeTab} onChange={setActiveTab}>
            <TabList className="flex border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-t-2xl">
              {[
                { icon: DocumentCheckIcon, label: 'Discharge', badge: selectedPowerNos.size || null },
                { icon: ClockIcon, label: 'Pending', badge: pendingCount || null },
                { icon: CheckCircleIcon, label: 'Completed' },
              ].map(({ icon: Icon, label, badge }) => (
                <Tab
                  key={label}
                  className={({ selected }) =>
                    `flex-1 px-2 py-3 sm:px-4 sm:py-3.5 qhd:px-6 qhd:py-4 text-xs sm:text-sm qhd:text-base font-semibold border-b-2 transition-colors duration-200 flex items-center justify-center gap-1.5 sm:gap-2 ${
                      selected
                        ? 'text-primary dark:text-primary-light border-primary dark:border-primary-light bg-white dark:bg-gray-800'
                        : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                    }`
                  }
                >
                  <Icon className="hidden sm:block h-5 w-5 qhd:h-6 qhd:w-6" />
                  <span>{label}</span>
                  {badge > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 qhd:min-w-[1.5rem] qhd:h-6 px-1.5 rounded-full bg-primary text-white text-xs qhd:text-sm font-bold tabular-nums">
                      {badge}
                    </span>
                  )}
                </Tab>
              ))}
            </TabList>

            <TabPanels>
              {/* DISCHARGE TAB */}
              <TabPanel className="p-4 qhd:p-6 min-h-[24rem]">
                {/* No agency selected warning (multi-agency users) */}
                {agencies.length > 1 && !effectiveAgencyId && (
                  <OathAlert variant="warning" className="mb-4">
                    <div className="flex items-center gap-2">
                      <ExclamationTriangleIcon className="h-4 w-4 qhd:h-5 qhd:w-5 shrink-0" />
                      <span className="text-sm qhd:text-base">
                        Please select an agency above before entering powers.
                      </span>
                    </div>
                  </OathAlert>
                )}

                {/* Active draft banner */}
                {activeReportId && activeDraftMeta && (
                  <OathAlert variant="primary" className="mb-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <DocumentDuplicateIcon className="h-4 w-4 qhd:h-5 qhd:w-5 shrink-0" />
                        <span className="text-sm qhd:text-base truncate">
                          Working on draft <strong>{activeDraftMeta.reportNumber || activeReportId}</strong>
                          {activeDraftMeta.powerCount != null && (
                            <> ({activeDraftMeta.powerCount} item{activeDraftMeta.powerCount !== 1 ? 's' : ''})</>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          onClick={handleAbandonActiveDraft}
                          className="text-xs qhd:text-sm font-semibold text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                        >
                          Abandon
                        </button>
                        <button
                          type="button"
                          onClick={handleStartFresh}
                          className="text-xs qhd:text-sm font-semibold text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-300"
                        >
                          Start Fresh
                        </button>
                      </div>
                    </div>
                  </OathAlert>
                )}

                {/* Open drafts list (when no draft is active) */}
                {!activeReportId && !isLoadingDrafts && drafts.length > 0 && (
                  <DraftsList
                    drafts={drafts}
                    onResume={handleResumeDraft}
                    onAbandon={abandonDraft}
                  />
                )}

                {isLoadingQueue ? (
                  <div className="flex flex-col items-center justify-center py-16">
                    <OathSpinner size="md" className="text-primary" />
                    <p className="mt-3 text-sm qhd:text-base text-gray-500 dark:text-gray-400">
                      Loading saved selections...
                    </p>
                  </div>
                ) : (
                  <PowerNumberEntry
                    selectedPowerNos={selectedPowerNos}
                    onSelectionChange={setSelectedPowerNos}
                    onAddUnverifiedPowers={addUnverifiedPowers}
                    onSaveAsDraft={!activeReportId ? handleCreateDraft : undefined}
                    agencyId={effectiveAgencyId}
                    powerDetailsMap={powerDetailsMap}
                    onPowerDetailsUpdate={updatePowerDetails}
                    clientValidationMap={clientValidationMap}
                    proofsByPower={proofsByPower}
                    onUploadProof={handleOpenProofModal}
                    onSubmitDischarge={handleSubmitDischarge}
                    isSubmitting={isValidating || isSubmitting}
                    reason={reason}
                    onReasonChange={setReason}
                    agentComments={agentComments}
                    onCommentsChange={setAgentComments}
                    savingPowerNos={savingPowerNos}
                  />
                )}
              </TabPanel>

              {/* PENDING TAB (Pending Review + Failed) */}
              <TabPanel className="p-4 qhd:p-6">
                <DischargeHistoryTable isActive={activeTab === DISCHARGE_TABS.PENDING} statuses={HISTORY_TAB_STATUSES[DISCHARGE_TABS.PENDING]} onItemsLoaded={handleHistoryItemsLoaded} />
              </TabPanel>

              {/* COMPLETED TAB */}
              <TabPanel className="p-4 qhd:p-6">
                <DischargeHistoryTable isActive={activeTab === DISCHARGE_TABS.COMPLETED} statuses={HISTORY_TAB_STATUSES[DISCHARGE_TABS.COMPLETED]} onItemsLoaded={handleHistoryItemsLoaded} />
              </TabPanel>

            </TabPanels>
          </TabGroup>
        </OathContentCard>

      {/* Confirm Discharge Modal */}
      <ConfirmDischargeModal
        isOpen={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        validationResult={validationResult}
        totalLiability={totalLiability}
        reason={reason}
        agentComments={agentComments}
        proofsByPower={proofsByPower}
        powerDetailsMap={powerDetailsMap}
        onConfirm={handleConfirmSubmit}
        isSubmitting={isSubmitting}
      />

      {/* Proof Upload Modal */}
      <ProofUploadModal
        isOpen={proofModalOpen}
        onClose={() => {
          setProofModalOpen(false);
          setProofModalPower(null);
        }}
        power={proofModalPower}
        existingFileId={proofsByPower.get(proofModalPower?.powerNo)?.fileId || null}
        onProofUpdated={handleProofUpdated}
      />
    </OathPageWrapper>
  );
};

export default withErrorBoundary(DischargePage, {
  title: 'Discharge Error',
  message: 'An error occurred in the Discharge module. Please try again.',
});
